import { Frequencies_Enum } from '@main/graphql/types.generated';
import { dayjs } from '@main/shared/utils';

export const recurringVendorQuestionnaireFrequencies = [
  Frequencies_Enum.Monthly,
  Frequencies_Enum.Quarterly,
  Frequencies_Enum.Biannual,
  Frequencies_Enum.Annual,
  Frequencies_Enum.TwoYears,
] as const satisfies Frequencies_Enum[];

export const vendorQuestionnaireFrequencies = [
  Frequencies_Enum.Indefinite,
  ...recurringVendorQuestionnaireFrequencies,
] as const satisfies Frequencies_Enum[];

const recurringVendorQuestionnaireFrequencySet = new Set<string>(
  recurringVendorQuestionnaireFrequencies,
);

export type RecurringVendorQuestionnaireFrequency =
  (typeof recurringVendorQuestionnaireFrequencies)[number];

export type VendorQuestionnaireFrequency = (typeof vendorQuestionnaireFrequencies)[number];

const cadenceMonths: Record<RecurringVendorQuestionnaireFrequency, number> = {
  [Frequencies_Enum.Monthly]: 1,
  [Frequencies_Enum.Quarterly]: 3,
  [Frequencies_Enum.Biannual]: 6,
  [Frequencies_Enum.Annual]: 12,
  [Frequencies_Enum.TwoYears]: 24,
};

export function isRecurringVendorQuestionnaireFrequency(
  value: string,
): value is RecurringVendorQuestionnaireFrequency {
  return recurringVendorQuestionnaireFrequencySet.has(value);
}

export function isRecurringVendorQuestionnaireSchedule(
  schedule: { frequency: string } | null | undefined,
): schedule is { frequency: RecurringVendorQuestionnaireFrequency } {
  return Boolean(schedule && isRecurringVendorQuestionnaireFrequency(schedule.frequency));
}

export function parseRecurringVendorQuestionnaireDueDate(value: string): string | null {
  const dueDate = dayjs.utc(value);
  return dueDate.isValid() ? dueDate.toISOString() : null;
}

export function getNextRecurringVendorQuestionnaireSendAt(
  anchor: string,
  frequency: string,
): string {
  if (!isRecurringVendorQuestionnaireFrequency(frequency)) {
    throw new Error(`Unsupported recurring questionnaire frequency: ${frequency}`);
  }

  const anchorDate = dayjs.utc(anchor);
  if (!anchorDate.isValid()) {
    throw new Error(`Invalid recurring questionnaire timestamp: ${anchor}`);
  }

  return anchorDate.add(cadenceMonths[frequency], 'month').toISOString();
}

export function getFutureRecurringVendorQuestionnaireSendAt(input: {
  frequency: string;
  lastSentAt?: string | null;
  now?: string;
}): string {
  const now = input.now ?? dayjs.utc().toISOString();
  const nowDate = dayjs.utc(now);
  if (!nowDate.isValid()) {
    throw new Error(`Invalid recurring questionnaire timestamp: ${now}`);
  }

  const nextSendAtFromLastSent = input.lastSentAt
    ? getNextRecurringVendorQuestionnaireSendAt(input.lastSentAt, input.frequency)
    : null;

  return nextSendAtFromLastSent && dayjs.utc(nextSendAtFromLastSent).isAfter(nowDate)
    ? nextSendAtFromLastSent
    : getNextRecurringVendorQuestionnaireSendAt(now, input.frequency);
}
